import type { Karta, Lekcja } from './typy';

/**
 * Sześć lekcji prototypu. Każda seria prowadzi do jednego zadania CKE
 * (liczby i dane z oryginału), a powtórki wracają do umiejętności przez INNE
 * zadania CKE. Karty z `zadanieId: null` to ćwiczenia pomocnicze — pokazywane
 * z etykietą „to nie jest zadanie CKE”.
 */

// ============================================================ Matematyka

const M1 = 'mat-2209-pp-1';
const M1_WYRAZENIE = 'Zadanie: $(1 + 3 \\cdot 2^{-1})^{-2}$';

const ULAMKI: Karta[] = [
  {
    podpowiedz: "Wewnątrz nawiasu wykonujemy kolejno potęgowanie, mnożenie, dodawanie. Potęgę całego nawiasu liczymy na końcu.",
    id: 'm1-polecenie',
    zadanieId: M1,
    etap: 'polecenie',
    rodzaj: 'wybor',
    kontekst: M1_WYRAZENIE,
    pytanie: "Który fragment tego wyrażenia obliczysz jako pierwszy?",
    opcje: ["Potęgę $2^{-1}$ w nawiasie","Sumę $1 + 3$ w nawiasie","Potęgę całego nawiasu o wykładniku −2"],
    poprawna: 0,
    dlaczegoNie: {"1":"W nawiasie potęgowanie i mnożenie mają pierwszeństwo przed dodawaniem.","2":"Potęga o wykładniku −2 wymaga najpierw obliczenia wartości wewnątrz nawiasu."},
    wyjasnienie: "Najpierw oblicz $2^{-1} = \\frac12$. Następnie pomnóż przez 3, dodaj 1 i dopiero podnieś cały wynik do potęgi −2.",
  },
  {
    podpowiedz: "W nawiasie: potęga, potem mnożenie, potem dodawanie. Na końcu potęga całego nawiasu.",
    id: 'm1-kolejnosc',
    zadanieId: M1,
    etap: 'zasada',
    rodzaj: 'kolejnosc',
    kontekst: M1_WYRAZENIE,
    pytanie: 'Ułóż działania w kolejności, w jakiej je wykonasz.',
    elementy: [
      '$2^{-1}$ — potęga w nawiasie',
      '$3 \\cdot (\\ldots)$ — mnożenie',
      '$1 + (\\ldots)$ — dodawanie w nawiasie',
      '$(\\ldots)^{-2}$ — potęga całego nawiasu',
    ],
    latwiejsza: 'm1-kolejnosc-l',
    wyjasnienie: 'Najpierw nawias, a w nawiasie: potęga → mnożenie → dodawanie. Potęga całego nawiasu na końcu.',
  },
  {
    id: 'm1-kolejnosc-l',
    zadanieId: M1,
    etap: 'zasada',
    rodzaj: 'wybor',
    kontekst: 'Nawias: $1 + 3 \\cdot 2^{-1}$',
    pytanie: 'Co liczysz wcześniej: dodawanie $1 + 3$ czy mnożenie $3 \\cdot 2^{-1}$?',
    opcje: ['Mnożenie $3 \\cdot 2^{-1}$', 'Dodawanie $1 + 3$', 'Obojętnie — wynik będzie ten sam'],
    poprawna: 0,
    dlaczegoNie: {
      1: 'Dodawanie idzie po mnożeniu. $(1 + 3) \\cdot \\frac12 = 2$, a poprawnie $1 + \\frac32 = \\frac52$.',
      2: 'Kolejność zmienia wynik: $2$ zamiast $\\frac52$.',
    },
    wyjasnienie: 'Mnożenie ma pierwszeństwo przed dodawaniem — dlatego najpierw $3 \\cdot 2^{-1}$.',
  },
  {
    id: 'm1-potega',
    zadanieId: M1,
    etap: 'fragment',
    rodzaj: 'wpis',
    kontekst: 'Pierwszy krok zadania',
    pytanie: "Ile to jest $2^{-1}$?",
    oczekiwane: { typ: 'liczba', wartosc: 0.5 },
    klawiatura: 'mat',
    typoweBledy: {
      '-2': 'To jest $2 \\cdot (-1)$. Ujemny wykładnik oznacza odwrotność: $2^{-1} = \\frac12$.',
      '-1/2': 'Minus w wykładniku nie zmienia znaku liczby — daje odwrotność: $\\frac12$.',
      '-0.5': 'Minus w wykładniku nie zmienia znaku liczby — daje odwrotność: $\\frac12$.',
    },
    latwiejsza: 'm1-potega-l',
    podpowiedz: 'Ujemny wykładnik: $a^{-1} = \\frac{1}{a}$.',
    wyjasnienie: '$2^{-1} = \\frac{1}{2}$ — potęga o wykładniku $-1$ to odwrotność liczby.',
  },
  {
    id: 'm1-potega-l',
    zadanieId: M1,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'Czym jest $a^{-1}$ (dla $a \\ne 0$)?',
    opcje: ['Odwrotnością: $\\frac{1}{a}$', 'Liczbą przeciwną: $-a$', 'Zerem'],
    poprawna: 0,
    dlaczegoNie: { 1: 'Liczba przeciwna to $-a$ — ale wykładnik $-1$ nie zmienia znaku, tylko odwraca.' },
    wyjasnienie: '$a^{-1} = \\frac1a$. Na przykład $2^{-1} = \\frac12$, $5^{-1} = \\frac15$.',
  },
  {
    podpowiedz: "Porównuj każdy wiersz z poprzednim. Szukasz pierwszej zmiany, która nie zachowuje wartości wyrażenia.",
    kontekst: "$2^{-1}=\\frac12$. Mnożenie wykonujemy przed dodawaniem.",
    id: 'm1-blad',
    givenFirstLine: true,
    zadanieId: M1,
    etap: 'fragment',
    rodzaj: 'blad',
    pytanie: "W którym wierszu po raz pierwszy pojawia się błąd?",
    linie: ['$1 + 3 \\cdot 2^{-1}$', '$= 4 \\cdot 2^{-1}$', '$= 4 \\cdot \\frac{1}{2} = 2$'],
    bledna: 1,
    wyjasnienie: "Błąd pojawia się w wierszu 2: dodano 1 + 3, choć najpierw trzeba pomnożyć $3\\cdot\\frac12$. Wiersz 3 poprawnie oblicza błędnie otrzymane wyrażenie — nie jest pierwszym błędem. Poprawnie: $1+\\frac32=\\frac52$.",
  },
  {
    podpowiedz: "Najpierw $3\\cdot\\frac12$. Potem dodaj 1, zapisując ją jako $\\frac22$.",
    id: 'm1-nawias',
    zadanieId: M1,
    etap: 'fragment',
    rodzaj: 'wpis',
    pytanie: 'Oblicz nawias: $1 + 3 \\cdot \\frac{1}{2}$',
    oczekiwane: { typ: 'liczba', wartosc: 2.5 },
    klawiatura: 'mat',
    typoweBledy: { '2': 'To wynik $(1 + 3) \\cdot \\frac12$ — mnożenie ma pierwszeństwo.' },
    latwiejsza: 'm1-kolejnosc-l',
    zPamieci: true,
    wyjasnienie: '$3 \\cdot \\frac12 = \\frac32$, a $1 + \\frac32 = \\frac52$.',
  },
  {
    podpowiedz: "Odwrotność ułamka otrzymasz, zamieniając licznik z mianownikiem.",
    kontekst: "Nawias ma wartość $\\frac52$. Teraz przygotowujemy się do potęgi o ujemnym wykładniku.",
    id: 'm1-odwrotnosc',
    zadanieId: M1,
    etap: 'fragment',
    rodzaj: 'wpis',
    rusztowanie: true,
    pytanie: 'Najpierw odwrotność: $\\left(\\frac{5}{2}\\right)^{-1} = ?$',
    oczekiwane: { typ: 'liczba', wartosc: 0.4 },
    klawiatura: 'mat',
    wyjasnienie: 'Odwrotność ułamka: zamieniamy licznik z mianownikiem — $\\frac25$.',
  },
  {
    kontekst: "Nawias ma wartość $\\frac52$. Reguła: $a^{-2}=(\\frac1a)^2$ dla $a\\ne0$.",
    id: 'm1-kwadrat',
    zadanieId: M1,
    etap: 'fragment',
    rodzaj: 'wpis',
    pytanie: 'Ostatni krok: $\\left(\\frac{5}{2}\\right)^{-2} = ?$',
    oczekiwane: { typ: 'liczba', wartosc: 0.16 },
    klawiatura: 'mat',
    typoweBledy: {
      '25/4': 'To $\\left(\\frac52\\right)^{2}$ — minus w wykładniku oznacza jeszcze odwrócenie ułamka.',
      '6.25': 'To $\\left(\\frac52\\right)^{2}$ — minus w wykładniku oznacza jeszcze odwrócenie ułamka.',
      '-25/4': 'Minus w wykładniku nie daje liczby ujemnej — odwraca ułamek.',
    },
    latwiejsza: 'm1-odwrotnosc',
    podpowiedz: 'Odwróć ułamek, potem podnieś do kwadratu.',
    wyjasnienie: '$\\left(\\frac52\\right)^{-2} = \\left(\\frac25\\right)^{2} = \\frac{4}{25}$.',
  },
  {
    kontekst: "Po odwróceniu ułamka otrzymaliśmy $\\frac25$. Pozostaje obliczyć $(\\frac25)^2$.",
    id: 'm1-sprawdz',
    zadanieId: M1,
    etap: 'sprawdzenie',
    rodzaj: 'wybor',
    pytanie: "Który zapis poprawnie podnosi ten ułamek do kwadratu?",
    opcje: ["$\\frac{2^2}{5^2}=\\frac4{25}$","$\\frac{2\\cdot2}{5\\cdot2}=\\frac4{10}$","$\\frac{2^2}{5}=\\frac45$"],
    poprawna: 0,
    wyjasnienie: "Do kwadratu podnosimy osobno licznik i mianownik. Mnożenie obu przez 2 tylko rozszerza ułamek, a nie podnosi go do kwadratu.",
  },
  {
    id: 'm1-zadanie',
    zadanieId: M1,
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: "Wybierz wynik całego wyrażenia. Jeśli potrzebujesz, rozwiń rozwiązanie poniżej.",
    koniec: { typ: 'abcd', poprawna: 1 },
    wyjasnienie: 'Odpowiedź B: $\\frac{4}{25}$ — zgodnie z kluczem CKE.',
  },
  // --- powtórka: inne zadanie CKE (maj 2026, zad. 1)
  {
    podpowiedz: "Potęgowanie i mnożenie wykonujemy przed dodawaniem.",
    id: 'm1-p1',
    zadanieId: 'mat-2605-pp-1',
    etap: 'zasada',
    rodzaj: 'wybor',
    kontekst: 'Zadanie: $\\sqrt{\\frac{25}{8}} \\cdot \\sqrt{2} + 2^{-1}$',
    pytanie: 'Które działanie wykonasz na samym końcu?',
    opcje: ['Dodawanie', 'Mnożenie pierwiastków', 'Potęgowanie $2^{-1}$'],
    poprawna: 0,
    wyjasnienie: 'Mnożenie i potęga mają pierwszeństwo — dodawanie jest ostatnie.',
  },
  {
    id: 'm1-p2',
    zadanieId: 'mat-2605-pp-1',
    etap: 'fragment',
    rodzaj: 'wpis',
    pytanie: 'Oblicz $\\sqrt{\\frac{25}{8}} \\cdot \\sqrt{2}$',
    oczekiwane: { typ: 'liczba', wartosc: 2.5 },
    klawiatura: 'mat',
    zPamieci: true,
    podpowiedz: 'Pomnóż pod jednym pierwiastkiem: $\\sqrt{\\frac{25}{8} \\cdot 2}$.',
    wyjasnienie: '$\\sqrt{\\frac{25}{8} \\cdot 2} = \\sqrt{\\frac{25}{4}} = \\frac52$.',
  },
  {
    podpowiedz: "Połącz pierwiastki w jeden iloczyn, a $2^{-1}$ zastąp przez $\\frac12$. Na końcu dodaj wyniki.",
    id: 'm1-p3',
    zadanieId: 'mat-2605-pp-1',
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Rozwiąż całe zadanie i wybierz odpowiedź.',
    koniec: { typ: 'abcd', poprawna: 2 },
    wyjasnienie: '$\\frac52 + \\frac12 = 3$ — odpowiedź C (klucz CKE, wersja A).',
  },
];

const M2 = 'mat-2405-pp-2';
const M2_WYRAZENIE = 'Zadanie: $\\left(\\frac{1}{16}\\right)^{8} \\cdot 8^{16}$, odpowiedzi: $2^{24}$, $2^{16}$, $2^{12}$, $2^{8}$';

const POTEGI: Karta[] = [
  {
    podpowiedz: "$16=2^4$ i $8=2^3$. Zapis z tą samą podstawą pozwala działać na małych wykładnikach.",
    id: 'm2-polecenie',
    zadanieId: M2,
    etap: 'polecenie',
    rodzaj: 'wybor',
    kontekst: M2_WYRAZENIE,
    pytanie: 'Wszystkie odpowiedzi to potęgi dwójki. Jaki plan jest najlepszy?',
    opcje: [
      'Zapisać $\\frac{1}{16}$ i $8$ jako potęgi dwójki',
      'Obliczyć $8^{16}$ na kalkulatorze',
      'Skrócić 16 z mianownika z wykładnikiem 16',
    ],
    poprawna: 0,
    dlaczegoNie: {
      1: 'Kalkulator prosty nie pokaże dokładnie tak dużej liczby, a odpowiedzi i tak są potęgami 2.',
      2: 'Liczby i wykładnika nie wolno „skracać” — to zupełnie różne miejsca w zapisie.',
    },
    wyjasnienie: "Jednakowa podstawa pozwala mnożyć i dodawać wykładniki. Dzięki temu nie trzeba obliczać wielkich liczb.",
  },
  {
    podpowiedz: "Wykładnik mówi, ile dwójek mnożymy. Sprawdź 2 · 2 · 2.",
    id: 'm2-dane',
    zadanieId: M2,
    etap: 'dane',
    rodzaj: 'wpis',
    rusztowanie: true,
    pytanie: '$8 = 2^{?}$ — jaki wykładnik?',
    oczekiwane: { typ: 'liczba', wartosc: 3 },
    klawiatura: 'mat',
    wyjasnienie: '$2 \\cdot 2 \\cdot 2 = 8$, więc $8 = 2^{3}$.',
  },
  {
    podpowiedz: "Mnóż kolejne dwójki: 2, 4, 8, 16. Policz użyte czynniki.",
    id: 'm2-dane2',
    zadanieId: M2,
    etap: 'dane',
    rodzaj: 'wpis',
    rusztowanie: true,
    pytanie: '$16 = 2^{?}$ — jaki wykładnik?',
    oczekiwane: { typ: 'liczba', wartosc: 4 },
    klawiatura: 'mat',
    wyjasnienie: '$2^{4} = 16$.',
  },
  {
    kontekst: "$16=2^4$. Ujemny wykładnik oznacza odwrotność: $a^{-n}=\\frac1{a^n}$ dla $a\\ne0$.",
    id: 'm2-ujemna',
    zadanieId: M2,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: '$\\frac{1}{16}$ jako potęga dwójki to:',
    opcje: ['$2^{-4}$', '$2^{4}$', '$-2^{4}$', '$2^{\\frac14}$'],
    poprawna: 0,
    dlaczegoNie: {
      1: '$2^{4} = 16$, a potrzebujemy odwrotności $\\frac{1}{16}$.',
      2: '$-2^{4} = -16$ — liczba ujemna, a $\\frac{1}{16}$ jest dodatnia.',
      3: '$2^{\\frac14}$ to pierwiastek czwartego stopnia z 2 — to nie $\\frac1{16}$.',
    },
    latwiejsza: 'm2-ujemna-l',
    wyjasnienie: '$\\frac{1}{16} = \\frac{1}{2^{4}} = 2^{-4}$ — odwrotność to ujemny wykładnik.',
  },
  {
    podpowiedz: "$a^{-1}=\\frac1a$, czyli wykładnik −1 oznacza odwrotność.",
    id: 'm2-ujemna-l',
    zadanieId: M2,
    etap: 'zasada',
    rodzaj: 'wpis',
    pytanie: '$\\frac{1}{2} = 2^{?}$ — jaki wykładnik?',
    oczekiwane: { typ: 'liczba', wartosc: -1 },
    klawiatura: 'mat',
    wyjasnienie: '$\\frac12 = 2^{-1}$. Tak samo $\\frac{1}{2^4} = 2^{-4}$.',
  },
  {
    kontekst: "Potęga potęgi: $(a^m)^n=a^{m\\cdot n}$. Przy takiej operacji mnożymy wykładniki.",
    id: 'm2-blad',
    zadanieId: M2,
    etap: 'zasada',
    rodzaj: 'blad',
    pytanie: "W którym wierszu po raz pierwszy źle zastosowano regułę potęgi potęgi?",
    linie: [
      '$\\left(\\frac{1}{16}\\right)^{8} = \\left(2^{-4}\\right)^{8}$',
      '$= 2^{-4 + 8}$',
      '$= 2^{4}$',
    ],
    bledna: 1,
    wyjasnienie: "Wiersz 2: zamiast dodać −4 + 8, trzeba pomnożyć $-4\\cdot8=-32$. Wiersz 3 poprawnie liczy sumę z błędnego wiersza 2. Poprawny wynik: $2^{-32}$.",
  },
  {
    podpowiedz: "W potędze potęgi mnożymy wykładniki. Liczba ujemna razy dodatnia daje liczbę ujemną.",
    id: 'm2-f1',
    zadanieId: M2,
    etap: 'fragment',
    rodzaj: 'wpis',
    pytanie: '$\\left(2^{-4}\\right)^{8} = 2^{?}$ — jaki wykładnik?',
    oczekiwane: { typ: 'liczba', wartosc: -32 },
    klawiatura: 'mat',
    typoweBledy: {
      '4': 'Dodałeś $-4 + 8$. Przy potędze potęgi wykładniki mnożymy.',
      '32': 'Zgubiony minus: $-4 \\cdot 8 = -32$.',
    },
    latwiejsza: 'm2-blad',
    wyjasnienie: '$-4 \\cdot 8 = -32$.',
  },
  {
    podpowiedz: "Pomnóż wykładniki: 3 · 16. To wykładnik wyniku, a nie jego podstawa.",
    id: 'm2-f2',
    zadanieId: M2,
    etap: 'fragment',
    rodzaj: 'wpis',
    zPamieci: true,
    pytanie: '$\\left(2^{3}\\right)^{16} = 2^{?}$ — jaki wykładnik?',
    oczekiwane: { typ: 'liczba', wartosc: 48 },
    klawiatura: 'mat',
    typoweBledy: { '19': 'Dodałeś $3 + 16$ — przy potędze potęgi mnożymy: $3 \\cdot 16$.' },
    wyjasnienie: '$3 \\cdot 16 = 48$.',
  },
  {
    id: 'm2-f3',
    zadanieId: M2,
    etap: 'fragment',
    rodzaj: 'wpis',
    pytanie: '$2^{-32} \\cdot 2^{48} = 2^{?}$ — jaki wykładnik?',
    oczekiwane: { typ: 'liczba', wartosc: 16 },
    klawiatura: 'mat',
    typoweBledy: {
      '-1536': 'Przy mnożeniu potęg o tej samej podstawie wykładniki DODAJEMY, nie mnożymy.',
      '80': 'Zgubiony minus: $-32 + 48 = 16$.',
    },
    podpowiedz: 'Iloczyn potęg o tej samej podstawie: $2^{a} \\cdot 2^{b} = 2^{a+b}$.',
    wyjasnienie: '$-32 + 48 = 16$, więc wynik to $2^{16}$.',
  },
  {
    kontekst: "Po przekształceniu obu czynników mamy $2^{-32}\\cdot2^{48}$.",
    id: 'm2-sprawdz',
    zadanieId: M2,
    etap: 'sprawdzenie',
    rodzaj: 'wybor',
    pytanie:
      "Który zapis poprawnie łączy potęgi o tej samej podstawie?",
    opcje: ["$2^{-32+48}=2^{16}$","$2^{(-32)\\cdot48}=2^{-1536}$","$2^{32+48}=2^{80}$"],
    poprawna: 0,
    wyjasnienie: "Mnożąc potęgi o tej samej podstawie, dodajemy wykładniki ze znakami: −32 + 48 = 16. Wykładniki mnoży się przy potędze potęgi.",
  },
  {
    id: 'm2-zadanie',
    zadanieId: M2,
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: "Wybierz wynik całego wyrażenia. Możesz przypomnieć sobie obliczenia poniżej.",
    koniec: { typ: 'abcd', poprawna: 1 },
    wyjasnienie: 'Odpowiedź B: $2^{16}$ — zgodnie z kluczem CKE (wersja A).',
  },
  // --- powtórki: inne zadania CKE
  {
    podpowiedz: "Dzieląc potęgi o tej samej podstawie, odejmujemy wykładniki: $a^m/a^n=a^{m-n}$.",
    id: 'm2-p1',
    zadanieId: 'mat-2505-pp-2',
    etap: 'fragment',
    rodzaj: 'wpis',
    zPamieci: true,
    pytanie: 'Oblicz $\\frac{5^{13}}{5^{12}}$',
    oczekiwane: { typ: 'liczba', wartosc: 5 },
    klawiatura: 'mat',
    typoweBledy: { '1': 'Iloraz potęg: odejmujemy wykładniki — $5^{13-12} = 5^{1} = 5$.' },
    wyjasnienie: '$5^{13 - 12} = 5$.',
  },
  {
    podpowiedz: "Podziel każdy składnik licznika przez $5^{12}$. Dostaniesz $5^0+5^1+5^2$. Pamiętaj: $5^0=1$.",
    id: 'm2-p2',
    zadanieId: 'mat-2505-pp-2',
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Rozwiąż całe zadanie i wybierz odpowiedź.',
    koniec: { typ: 'abcd', poprawna: 1 },
    wyjasnienie: '$1 + 5 + 25 = 31$ — odpowiedź B (klucz CKE, wersja A).',
  },
  {
    podpowiedz: "Zapisz $4^{12}=2^{24}$ i połącz z $5^{24}$ w $(2\\cdot5)^{24}$. Liczba $10^n$ ma jedynkę i n zer.",
    id: 'm2-p3',
    zadanieId: 'mat-2605-pp-5',
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Oceń prawdziwość obu stwierdzeń.',
    koniec: {
      typ: 'pf',
      poprawne: [true, true],
      zdania: [
        'Liczba $4^{12} \\cdot 5^{24}$ jest podzielna przez 20.',
        'Liczba $4^{12} \\cdot 5^{24}$ ma w zapisie dziesiętnym 25 cyfr.',
      ],
    },
    wyjasnienie: '$4^{12} \\cdot 5^{24} = 2^{24} \\cdot 5^{24} = 10^{24}$ — P, P (klucz CKE).',
  },
];

// ============================================================ Informatyka

const C1 = 'inf-2405-2.1';

const ZMIENNE: Karta[] = [
  {
    id: 'c1-polecenie',
    zadanieId: C1,
    etap: 'polecenie',
    rodzaj: 'wybor',
    kontekst: "Polecenie: dla n = 542102 podaj końcową wartość c oraz liczbę wykonań instrukcji `c ← c + b`. To dwa osobne pola tabeli.",
    pytanie: 'Co trzeba wpisać w tabeli dla n = 542102?',
    opcje: [
      'Końcowe c i liczbę wykonań `c ← c + b`',
      'Końcowe n i liczbę wszystkich obrotów pętli',
      'Ostatnie a i liczbę cyfr początkowego n',
    ],
    poprawna: 0,
    wyjasnienie: 'Dwie liczby w wierszu: końcowe c i to, ile razy wykonała się `c ← c + b`.',
  },
  {
    podpowiedz: "`mod` oznacza resztę z dzielenia, a `div` — iloraz całkowity. Python używa odpowiednio operatorów `%` i `//`.",
    id: 'c1-zasada',
    zadanieId: C1,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'W Pythonie `n mod 10` i `n div 10` zapiszesz jako:',
    opcje: ['`n % 10` i `n // 10`', '`n % 10` i `n / 10`', '`n mod 10` i `n div 10`'],
    poprawna: 0,
    dlaczegoNie: {
      1: '`/` w Pythonie daje liczbę z przecinkiem (54210.2), a `div` to część całkowita — `//`.',
      2: 'W Pythonie nie ma słów `mod` i `div` — są operatory `%` i `//`.',
    },
    wyjasnienie: '`%` — reszta z dzielenia (ostatnia cyfra), `//` — dzielenie całkowite (obcięcie cyfry).',
  },
  {
    id: 'c1-kod1',
    zadanieId: C1,
    etap: 'dane',
    rodzaj: 'kod',
    pytanie: 'Co wypisze ten program? (dwie linijki)',
    kod: 'n = 542102\nprint(n % 10)\nprint(n // 10)',
    wynik: ['2\n54210'],
    latwiejsza: 'c1-kod1-l',
    wyjasnienie: 'Ostatnia cyfra 542102 to 2, a po obcięciu jej zostaje 54210.',
  },
  {
    id: 'c1-kod1-l',
    zadanieId: C1,
    etap: 'dane',
    rodzaj: 'wybor',
    pytanie: 'Reszta z dzielenia 542102 przez 10 to:',
    opcje: ['2', '0', '54210'],
    poprawna: 0,
    wyjasnienie: 'Reszta z dzielenia przez 10 to zawsze ostatnia cyfra liczby.',
  },
  {
    kontekst: "Dla n = 542102 chcemy odczytać ostatnią cyfrę i usunąć ją, pozostawiając liczbę całkowitą. Użyj operatorów zgodnych z pseudokodem zadania.",
    id: 'c1-blad',
    zadanieId: C1,
    etap: 'zasada',
    rodzaj: 'blad',
    pytanie: "Który wiersz trzeba poprawić, aby n pozostało liczbą całkowitą po usunięciu ostatniej cyfry?",
    linie: ['a = n % 10', 'n = n / 10', 'b = b * 10'],
    bledna: 1,
    wyjasnienie: '`n / 10` daje 54210.2 (typ float). Algorytm wymaga dzielenia całkowitego: `n = n // 10`.',
  },
  {
    kontekst: "W tym algorytmie najpierw odczytujemy cyfrę, potem usuwamy ją z n, aktualizujemy wynik c i dopiero zwiększamy mnożnik b dla następnej cyfry.",
    id: 'c1-kolejnosc',
    zadanieId: C1,
    etap: 'zasada',
    rodzaj: 'kolejnosc',
    pytanie: "Ułóż instrukcje zgodnie z opisem jednego obrotu pętli.",
    elementy: [
      '`a ← n mod 10` — ostatnia cyfra',
      '`n ← n div 10` — obcięcie cyfry',
      'jeżeli a parzyste: `c ← c + b * (a div 2)`, inaczej `c ← c + b`',
      '`b ← b * 10`',
    ],
    rusztowanie: true,
    wyjasnienie: 'Bierzemy cyfrę, obcinamy ją, dopisujemy do c, przesuwamy mnożnik b o jedno miejsce.',
  },
  {
    podpowiedz: "Najpierw policz a // 2, potem pomnóż wynik przez b i dodaj dotychczasowe c. Wszystkie wartości masz w kodzie.",
    id: 'c1-kod2',
    zadanieId: C1,
    etap: 'fragment',
    rodzaj: 'kod',
    pytanie: 'Piąty obrót pętli dla n = 542102 (cyfra 4). Co wypisze program?',
    kod: 'a = 4\nb = 10000\nc = 1101\nc = c + b * (a // 2)\nprint(c)',
    wynik: ['21101'],
    latwiejsza: 'c1-kod2-l',
    wyjasnienie: '$4 // 2 = 2$, więc do c dochodzi $10000 \\cdot 2 = 20000$: $1101 + 20000 = 21101$.',
  },
  {
    id: 'c1-kod2-l',
    zadanieId: C1,
    etap: 'fragment',
    rodzaj: 'kod',
    pytanie: 'Co wypisze program?',
    kod: 'print(4 // 2)\nprint(10000 * 2)',
    wynik: ['2\n20000'],
    wyjasnienie: '`//` to dzielenie całkowite: 4 // 2 = 2.',
  },
  {
    kontekst: "Start: c = 0. Czytamy cyfry od prawej: 2, 0, 1; mnożniki b: 1, 10, 100. Dla parzystej cyfry a dodajemy b · (a // 2), dla nieparzystej dodajemy b.",
    id: 'c1-slad',
    zadanieId: C1,
    etap: 'fragment',
    rodzaj: 'wpis',
    zPamieci: true,
    pytanie: 'n = 542102. Ile wynosi c po trzech obrotach pętli (cyfry 2, 0, 1)?',
    oczekiwane: { typ: 'liczba', wartosc: 101 },
    klawiatura: 'mat',
    typoweBledy: { '3': 'Zapomniany mnożnik b: każda kolejna cyfra jest mnożona przez 1, 10, 100…' },
    latwiejsza: 'c1-slad-l',
    podpowiedz: 'Cyfra 2: +1·(2//2); cyfra 0: +10·(0//2); cyfra 1 (nieparzysta): +100.',
    wyjasnienie: '1 + 0 + 100 = 101.',
  },
  {
    id: 'c1-slad-l',
    zadanieId: C1,
    etap: 'fragment',
    rodzaj: 'wpis',
    pytanie: 'Pierwszy obrót: a = 2, b = 1, c = 0. Ile wynosi c po instrukcji `c ← c + b * (a div 2)`?',
    oczekiwane: { typ: 'liczba', wartosc: 1 },
    klawiatura: 'mat',
    wyjasnienie: '0 + 1 · (2 // 2) = 1.',
  },
  {
    podpowiedz: "Sprawdź, po której stronie warunku parzystości jest instrukcja `c ← c + b`.",
    id: 'c1-sprawdz',
    zadanieId: C1,
    etap: 'sprawdzenie',
    rodzaj: 'wybor',
    pytanie: "Co wyznacza liczbę wykonań instrukcji `c ← c + b` w algorytmie?",
    opcje: ['Liczba cyfr nieparzystych', 'Liczba cyfr parzystych', 'Liczba wszystkich odczytanych cyfr'],
    poprawna: 0,
    wyjasnienie: '`c ← c + b` jest w gałęzi „w przeciwnym razie”, czyli dla cyfr nieparzystych.',
  },
  {
    id: 'c1-zadanie',
    zadanieId: C1,
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Uzupełnij wiersz tabeli dla n = 542102.',
    koniec: {
      typ: 'wpis',
      oczekiwane: [
        { typ: 'liczba', wartosc: 121101 },
        { typ: 'liczba', wartosc: 2 },
      ],
      etykiety: ['c po wykonaniu algorytmu', 'liczba wykonań `c ← c + b`'],
    },
    python: {
      plik: '',
      nazwaPliku: '',
      oczekiwanyWynik: '121101 2',
      szablon:
        'n = 542102\nb = 1\nc = 0\nile = 0  # ile razy wykonano c = c + b\nwhile n > 0:\n    a = n % 10\n    n = n // 10\n    # uzupełnij warunek i obie gałęzie\n    b = b * 10\nprint(c, ile)\n',
    },
    wyjasnienie: 'Klucz CKE: c = 121101, 2 wykonania.',
  },
  // --- powtórki: inne zadanie CKE (maj 2025, zad. 1.1)
  {
    id: 'c1-p1',
    zadanieId: 'inf-2505-1.1',
    etap: 'fragment',
    rodzaj: 'kod',
    kontekst: 'Pierwsze cztery instrukcje funkcji `przestaw(n)` z arkusza 2025, w Pythonie.',
    pytanie: 'Co wypisze program?',
    kod: 'n = 43657688\nr = n % 100\na = r // 10\nb = r % 10\nn = n // 100\nprint(r, a, b, n)',
    wynik: ['88 8 8 436576'],
    wyjasnienie: '`% 100` — dwie ostatnie cyfry (88); z nich `// 10` i `% 10` to cyfry 8 i 8; `// 100` obcina dwie cyfry.',
  },
  {
    id: 'c1-p2',
    zadanieId: 'inf-2505-1.1',
    etap: 'fragment',
    rodzaj: 'kod',
    zPamieci: true,
    pytanie: "Dla n = 154005710 odczytujemy dwie ostatnie cyfry. Co wypisze program?",
    kod: 'n = 154005710\nr = n % 100\na = r // 10\nb = r % 10\nprint(r, a, b)',
    wynik: ['10 1 0'],
    wyjasnienie: "Reszta z dzielenia przez 100 to 10, a jej cyfry to 1 i 0. Program wypisze: 10 1 0.",
  },
];

const C2 = 'inf-inf-3.1';
const C2_DANE = 'Przedziały z informatora: A = [−2, 4], B = [−4, 3], C = [−3, 6], D = [0, 3], E = [1, 1], F = [7, 9]';

const WARUNKI: Karta[] = [
  {
    id: 'c2-polecenie',
    zadanieId: C2,
    etap: 'polecenie',
    rodzaj: 'wybor',
    kontekst: 'Zadanie 3.1: podaj dwie najmniejsze (różne) liczby, które są długościami przedziałów z pliku dane3.txt.',
    pytanie: "Długości to 4, 4, 7. Która para spełnia warunek „dwie najmniejsze różne długości”?",
    opcje: ["4 i 7","4 i 4","7 i 7"],
    poprawna: 0,
    dlaczegoNie: {"1":"Dwie czwórki mają tę samą wartość. Szukamy dwóch różnych wartości.","2":"Pomijasz najmniejszą długość 4 i powtarzasz 7."},
    wyjasnienie: "Powtórzenia pomijamy. Różne długości to 4 i 7, więc to szukana para.",
  },
  {
    id: 'c2-dane',
    zadanieId: C2,
    etap: 'dane',
    rodzaj: 'wpis',
    kontekst: "W tym zadaniu „długość” oznacza liczbę liczb całkowitych od a do b, z oboma końcami: b − a + 1. To inna definicja niż długość odcinka na osi.",
    pytanie: 'Jaka jest długość przedziału A = [−2, 4]? (długość = b − a + 1)',
    oczekiwane: { typ: 'liczba', wartosc: 7 },
    klawiatura: 'mat',
    typoweBledy: {
      '6': '$b - a = 6$ — trzeba dodać 1, bo liczymy oba końce.',
      '3': 'Odejmujemy a: $4 - (-2) + 1 = 7$.',
    },
    latwiejsza: 'c2-dane-l',
    wyjasnienie: '$4 - (-2) + 1 = 7$ — tak jak w informatorze: „przedział A ma długość 7”.',
  },
  {
    kontekst: "Liczymy liczby całkowite należące do przedziału, razem z końcami.",
    id: 'c2-dane-l',
    zadanieId: C2,
    etap: 'dane',
    rodzaj: 'wpis',
    pytanie: 'Przedział E = [1, 1]. Jaka jest jego długość (b − a + 1)?',
    oczekiwane: { typ: 'liczba', wartosc: 1 },
    klawiatura: 'mat',
    wyjasnienie: '$1 - 1 + 1 = 1$ — dlatego we wzorze jest „+ 1”.',
  },
  {
    id: 'c2-kod1',
    zadanieId: C2,
    etap: 'zasada',
    rodzaj: 'kod',
    pytanie: 'Czy liczba c należy do [a, b]? Co wypisze program? (dwie linijki)',
    kod: 'a, b = -2, 4\nc = 0\nprint(a <= c <= b)\nc = 5\nprint(a <= c <= b)',
    wynik: ['True\nFalse'],
    wyjasnienie: '0 leży między −2 a 4 (True), a 5 jest za prawym końcem (False).',
  },
  {
    podpowiedz: "Lewy koniec P musi leżeć na prawo od lewego końca Q (lub na nim), a prawy koniec P — na lewo od prawego końca Q. Oba warunki naraz.",
    id: 'c2-warunek',
    zadanieId: C2,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'Który warunek oznacza „P = [p1, p2] zawiera się w Q = [q1, q2]”?',
    opcje: ['`q1 <= p1 and p2 <= q2`', '`p1 <= q1 and q2 <= p2`', '`q1 <= p1 or p2 <= q2`'],
    poprawna: 0,
    dlaczegoNie: {
      1: 'To odwrotna sytuacja: Q zawiera się w P.',
      2: 'Przy `or` wystarczy jeden warunek — a P musi mieścić się w Q z obu stron.',
    },
    latwiejsza: 'c2-kod1',
    wyjasnienie: "`q1 <= p1` sprawdza lewą stronę, `p2 <= q2` prawą. Łączymy je przez `and`, bo cały przedział P musi mieścić się w Q.",
  },
  {
    id: 'c2-kod2',
    zadanieId: C2,
    etap: 'fragment',
    rodzaj: 'kod',
    kontekst: C2_DANE,
    pytanie: 'Czy A zawiera się w C? Co wypisze program?',
    kod: 'p1, p2 = -2, 4   # A\nq1, q2 = -3, 6   # C\nprint(q1 <= p1 and p2 <= q2)',
    wynik: ['True'],
    wyjasnienie: '−3 ≤ −2 i 4 ≤ 6 — oba warunki prawdziwe. Informator potwierdza: „C zawiera A”.',
  },
  {
    podpowiedz: "Instrukcje wykonują się od góry. Po przypisaniu min1 = dlug zmienna min1 nie pamięta już liczby 4.",
    kontekst: "Przed fragmentem: min1 = 4, min2 = 7, dlug = 2. Po dodaniu nowej długości chcemy zachować dwie najmniejsze różne wartości: 2 i 4.",
    id: 'c2-blad',
    zadanieId: C2,
    etap: 'zasada',
    rodzaj: 'blad',
    pytanie: "W którym wierszu min2 dostanie już nową wartość min1 zamiast poprzedniej?",
    linie: ['if dlug < min1:', '    min1 = dlug', '    min2 = min1'],
    bledna: 2,
    wyjasnienie:
      '`min2 = min1` kopiuje już NOWĄ wartość i stara najmniejsza ginie. Najpierw `min2 = min1`, dopiero potem `min1 = dlug` — tak jak w programie z informatora.',
  },
  {
    podpowiedz: "Nie musisz przepisywać całej pętli. Znajdź w pokazanej liście najmniejszą wartość, a potem najmniejszą wartość od niej większą.",
    id: 'c2-kod3',
    zadanieId: C2,
    etap: 'fragment',
    rodzaj: 'kod',
    kontekst: 'Długości przedziałów A–F z przykładu: 7, 8, 10, 4, 1, 3.',
    pytanie: 'Co wypisze program?',
    kod:
      'min1, min2 = 10**9, 10**9\nfor dlug in [7, 8, 10, 4, 1, 3]:\n    if dlug < min1:\n        min2 = min1\n        min1 = dlug\n    elif min1 < dlug < min2:\n        min2 = dlug\nprint(min1, min2)',
    wynik: ['1 3'],
    latwiejsza: 'c2-kod3-l',
    wyjasnienie: 'Najmniejsza długość to 1 (E), druga — 3 (F).',
  },
  {
    podpowiedz: "Po 7 najmniejsze jest 7. Gdy pojawia się 4, dotychczasowe 7 trzeba zachować jako drugą wartość.",
    id: 'c2-kod3-l',
    zadanieId: C2,
    etap: 'fragment',
    rodzaj: 'kod',
    pytanie: 'Krótsza lista długości. Co wypisze program?',
    kod:
      'min1, min2 = 10**9, 10**9\nfor dlug in [7, 4]:\n    if dlug < min1:\n        min2 = min1\n        min1 = dlug\n    elif min1 < dlug < min2:\n        min2 = dlug\nprint(min1, min2)',
    wynik: ['4 7'],
    wyjasnienie: 'Po 7: min1 = 7. Po 4: stara najmniejsza (7) przechodzi do min2, a min1 = 4.',
  },
  {
    kontekst: "Szukamy dwóch różnych długości. Aktualizacja drugiej to: `elif min1 < dlug < min2: min2 = dlug`.",
    id: 'c2-sprawdz',
    zadanieId: C2,
    etap: 'sprawdzenie',
    rodzaj: 'wybor',
    pytanie: 'Po co w `elif` jest warunek `min1 < dlug`?',
    opcje: [
      'Aby pominąć powtórzenia najmniejszej długości',
      'Aby pomijać długości większe od minimum',
      'Aby uwzględniać tylko kolejne wiersze pliku',
    ],
    poprawna: 0,
    wyjasnienie: 'Gdyby długość 1 wystąpiła dwa razy, bez tego warunku dostalibyśmy „1 1”, a zadanie chce dwóch różnych liczb.',
  },
  {
    id: 'c2-zadanie',
    zadanieId: C2,
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: "To zadanie wymaga przetworzenia pliku z 2023 parami liczb. Uruchom program albo rozwiń rozwiązanie poniżej — wyniku nie da się ustalić z samego przykładu A–F.",
    koniec: {
      typ: 'wpis',
      oczekiwane: [{ typ: 'tekst', warianty: ['3 4', '3,4', '3;4'] }],
      etykiety: ['dwie liczby oddzielone spacją'],
    },
    python: {
      plik: 'nauka/dane3.txt',
      nazwaPliku: 'dane3.txt',
      oczekiwanyWynik: '3 4',
      szablon:
        'min1, min2 = 10**9, 10**9\nwith open("dane3.txt") as f:\n    for linia in f:\n        a, b = map(int, linia.split())\n        dlug = b - a + 1\n        # uzupełnij warunki\nprint(min1, min2)\n',
    },
    wyjasnienie: 'Oficjalna odpowiedź z informatora: 3 4.',
  },
  // --- powtórki: inne zadanie CKE (maj 2024, zad. 2.1 — gałąź warunku)
  {
    id: 'c2-p1',
    zadanieId: 'inf-2405-2.1',
    etap: 'zasada',
    rodzaj: 'kod',
    kontekst: 'Warunek z algorytmu „Cyfry” (arkusz 2024) w Pythonie.',
    pytanie: 'Co wypisze program?',
    kod: 'a = 7\nif a % 2 == 0:\n    print("parzysta")\nelse:\n    print("nieparzysta")',
    wynik: ['nieparzysta'],
    wyjasnienie: '7 % 2 = 1, warunek fałszywy — wykonuje się `else`.',
  },
  {
    kontekst: "W każdym obrocie pętli przeglądamy jedną cyfrę. Dla nieparzystej wykonujemy `c ← c + b`, a dla parzystej inną instrukcję.",
    id: 'c2-p2',
    zadanieId: 'inf-2405-2.1',
    etap: 'fragment',
    rodzaj: 'wpis',
    zPamieci: true,
    pytanie:
      "Cyfry liczby to: 8 7 6 5 4 3 2 1 0 1 2 3 4 5 6 7 8. Ile razy wykona się instrukcja dla cyfry nieparzystej?",
    oczekiwane: { typ: 'liczba', wartosc: 8 },
    klawiatura: 'mat',
    podpowiedz: 'Gałąź „w przeciwnym razie” = cyfra nieparzysta. Policz cyfry nieparzyste.',
    wyjasnienie: 'Cyfry nieparzyste: 7, 5, 3, 1, 1, 3, 5, 7 — 8 razy (klucz CKE).',
  },
];

// ============================================================ Biznes i zarządzanie

const B1 = 'biz-2604-1';

const PRZEDSIEBIORCZOSC: Karta[] = [
  {
    kontekst: "Polecenie: nazwij cechę osoby przedsiębiorczej i uzasadnij ją przykładem działania Agnieszki z opisu.",
    id: 'b1-polecenie',
    zadanieId: B1,
    etap: 'polecenie',
    rodzaj: 'wybor',
    pytanie: "Która forma odpowiedzi spełnia oba wymagania polecenia?",
    opcje: [
      'Cecha i działanie Agnieszki z opisu',
      'Cecha i jej definicja z podręcznika',
      'Ocena decyzji i własna rada dla Agnieszki',
    ],
    poprawna: 0,
    dlaczegoNie: { 1: 'Definicja nie zastępuje przykładu konkretnego działania Agnieszki.' },
    wyjasnienie: 'Klucz CKE: 1 pkt za jedną poprawną cechę wraz z odpowiednim przykładem działania.',
  },
  {
    id: 'b1-dane',
    zadanieId: B1,
    etap: 'dane',
    rodzaj: 'wybor',
    pytanie: "Które z poniższych zdań opisuje podjęte działanie Agnieszki?",
    opcje: [
      'Zdecydowała się na studia w innym mieście',
      'Lokalna uczelnia oferuje przeciętny poziom nauczania',
      'Mogłaby pozostać blisko domu',
    ],
    poprawna: 0,
    dlaczegoNie: {
      1: 'To opis uczelni, a nie coś, co zrobiła Agnieszka.',
      2: '„Mogłaby” — to możliwość, której nie wybrała.',
    },
    latwiejsza: 'b1-dane-l',
    wyjasnienie: 'Przykład działania to decyzja i wyjazd mimo trudności.',
  },
  {
    id: 'b1-dane-l',
    zadanieId: B1,
    etap: 'dane',
    rodzaj: 'wybor',
    pytanie: "Które zdanie opisuje działanie, a nie cechę miejsca?",
    opcje: ['Wyjechała na studia', 'Uczelnia jest prestiżowa', 'Klub jest duży'],
    poprawna: 0,
    wyjasnienie: 'Działanie to czasownik o Agnieszce: zdecydowała, wyjechała, podjęła pracę.',
  },
  {
    kontekst: "Agnieszka sama zdecydowała o wyjeździe na lepszą uczelnię. Wiedziała, że oznacza to większe koszty, stres i rozłąkę z rodziną.",
    id: 'b1-zasada',
    zadanieId: B1,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'Która cecha pasuje do wyjazdu mimo wyższych kosztów, stresu i rozłąki?',
    opcje: ['Odwaga', 'Oszczędność', 'Uległość'],
    poprawna: 0,
    dlaczegoNie: {
      1: 'Wybrała droższą opcję — oszczędność nie pasuje do tego działania.',
      2: 'Decyzję podjęła sama, po rozmowach — to nie posłuszeństwo.',
    },
    wyjasnienie: 'Klucz CKE podaje m.in. odwagę, zdolność podejmowania decyzji, ambicję i pracowitość.',
  },
  {
    kontekst: "Pozostanie w domu oznacza niższe koszty i bliskość rodziny. Wyjazd daje prestiżową uczelnię i lepsze kluby sportowe, ale większe wydatki.",
    id: 'b1-decyzja',
    zadanieId: B1,
    etap: 'fragment',
    rodzaj: 'wybor',
    wariant: 'decyzja',
    pytanie: "Która korzyść przemawia za wyjazdem Agnieszki?",
    opcje: [
      'Rozwój nauki i kariery sportowej',
      'Ograniczenie kosztów i stresu',
      'Bliskość rodziny i znanego otoczenia',
    ],
    poprawna: 0,
    wyjasnienie: 'Niższe koszty i bliskość rodziny to argumenty ZA zostaniem. Przedsiębiorczy wybór waży korzyści i ryzyko.',
  },
  {
    kontekst: "Odpowiedź powinna łączyć cechę z konkretną decyzją, która ją pokazuje. Agnieszka wybrała prestiżową uczelnię i lepsze kluby mimo trudności.",
    id: 'b1-blad',
    zadanieId: B1,
    etap: 'sprawdzenie',
    rodzaj: 'blad',
    pytanie: "Który wiersz trzeba doprecyzować, aby wskazywał działanie świadczące o ambicji?",
    linie: ['Cecha: ambicja.', 'Przykład: Agnieszka jest aktywną sportsmenką.'],
    bledna: 1,
    wyjasnienie:
      'Bycie sportsmenką to opis, a nie działanie pokazujące ambicję. Na 1 pkt: „wybrała prestiżową uczelnię i lepsze kluby sportowe”.',
  },
  {
    id: 'b1-zadanie',
    zadanieId: B1,
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Napisz własną odpowiedź do zadania 1.1: cecha + przykład działania Agnieszki.',
    koniec: {
      typ: 'otwarta',
      kryteria: ['Nazwana cecha osoby przedsiębiorczej', 'Przykład DZIAŁANIA Agnieszki z opisu'],
      slowa: [
        ['odwag', 'ryzyk', 'ambic', 'ambitn', 'pracowit', 'decyz', 'zdecydowan', 'determin', 'odpowiedzial', 'samodziel'],
        ['wyjech', 'wyjazd', 'wyjeżdż', 'prestiż', 'dorywcz', 'podjęła prac', 'zdecydowała', 'wybrała', 'opuści'],
      ],
      wzorcowe: [
        'Odwaga – nie boi się wyzwań i potrafi podjąć ryzyko, skoro opuszcza rodzinne miasto mimo trudności.',
        'Zdolność podejmowania decyzji – po analizie dwóch możliwości wybrała trudniejszą, ale z lepszymi perspektywami.',
        'Ambicja – wybrała prestiżową uczelnię i lepsze kluby sportowe.',
        'Pracowitość – wymagające studia i praca w trakcie studiów.',
      ],
    },
    wyjasnienie: 'Porównaj z przykładami z klucza CKE i oceń: czy masz cechę I działanie z tekstu?',
  },
  {
    podpowiedz: "Innowacja produktowa zmienia ofertę; procesowa — sposób wytwarzania; marketingowa — sposób promowania lub sprzedaży.",
    id: 'b1-pomoc',
    zadanieId: null,
    etap: 'pomocnicze',
    rodzaj: 'wybor',
    pytanie: 'Fabryka mebli zastąpiła ręczne cięcie desek maszyną sterowaną komputerem. Jaki to rodzaj innowacji?',
    opcje: ['Procesowa', 'Produktowa', 'Marketingowa'],
    poprawna: 0,
    wyjasnienie: 'Zmienia się JAK firma produkuje, a nie CO oferuje — innowacja procesowa.',
  },
  // --- powtórka: inne zadanie CKE (1.2 z tego samego arkusza)
  {
    id: 'b1-p1',
    zadanieId: 'biz-2604-1.2',
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'Ćwiczenie do zadania 1.2: jaką kompetencję rozwija Agnieszka, planując wydatki na utrzymanie w granicach swoich dochodów?',
    opcje: ['Zarządzanie finansami', 'Organizowanie czasu pracy', 'Rozwiązywanie konfliktów'],
    poprawna: 0,
    wyjasnienie: 'Klucz CKE: zarządzanie finansami — planowanie, odpowiedzialność, bezpieczeństwo finansowe.',
  },
  {
    id: 'b1-p2',
    zadanieId: 'biz-2604-1.2',
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Zadanie 1.2: podaj kompetencję i wyjaśnij, dlaczego jest ważna we współczesnym świecie.',
    koniec: {
      typ: 'otwarta',
      kryteria: ['Nazwana kompetencja przedsiębiorcza', 'Wyjaśnienie, dlaczego jest ważna dziś'],
      slowa: [
        ['finans', 'pieniędz', 'budżet', 'planowan', 'organizac', 'czas', 'samodziel'],
        ['odpowiedzial', 'bezpiecz', 'stabiliz', 'decyz', 'planow', 'rynek', 'współczes', 'dziś', 'zmian'],
      ],
      wzorcowe: [
        'Kompetencja: zarządzanie finansami. Agnieszka dorabia na część kosztów utrzymania, uczy się planowania i podejmowania decyzji, podnosi odpowiedzialność oraz zapewnia sobie stabilizację i bezpieczeństwo finansowe.',
      ],
    },
    wyjasnienie: 'Klucz CKE: punkt za odniesienie się do roli kompetencji we współczesnym świecie.',
  },
];

const B2 = 'biz-2604-5';

const KOMUNIKACJA: Karta[] = [
  {
    id: 'b2-polecenie',
    zadanieId: B2,
    etap: 'polecenie',
    rodzaj: 'wybor',
    kontekst: "Polecenie: wyjaśnij, dlaczego warto chwalić publicznie oraz dlaczego warto przekazywać poważną krytykę w cztery oczy.",
    pytanie: "Które elementy powinna zawierać pełna odpowiedź?",
    opcje: [
      'Skutek publicznej pochwały i prywatnej krytyki',
      'Skutek prywatnej krytyki i przykład rozmowy',
      'Treść ogólnej zasady i dwa wyjątki od niej',
    ],
    poprawna: 0,
    dlaczegoNie: { 2: 'Wyjątki to osobne zadanie 5.2.' },
    wyjasnienie: 'Klucz CKE: 1 pkt za wyjaśnienie DWÓCH aspektów zasady.',
  },
  {
    podpowiedz: "Tekst odróżnia drobne poprawki od poważnej krytyki. Czy wskazanie literówki jest oceną pracownika?",
    id: 'b2-dane',
    zadanieId: B2,
    etap: 'dane',
    rodzaj: 'wybor',
    wariant: 'decyzja',
    pytanie: 'Szefowa mówi na zebraniu: „Na szóstym slajdzie masz literówkę”. Czy łamie zasadę z tekstu?',
    opcje: [
      'Nie — jest to drobna poprawka',
      'Tak — uwaga pada przy zespole',
      'Tak — odnosi się do czyjejś pracy',
    ],
    poprawna: 0,
    wyjasnienie: 'Tekst odróżnia drobne poprawki i dyskusje od krytyki. Poprawki mogą paść na spotkaniu.',
  },
  {
    id: 'b2-zasada',
    zadanieId: B2,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'Dlaczego krytykować w cztery oczy?',
    opcje: [
      'Rozmowa prywatna ogranicza presję widowni i reakcję obronną',
      'Rozmowa prywatna usuwa potrzebę wskazania konkretnego błędu',
      'Rozmowa prywatna zastępuje wyjaśnienie problemu oceną osoby',
    ],
    poprawna: 0,
    latwiejsza: 'b2-zasada-l',
    wyjasnienie: "Rozmowa prywatna pozwala skupić się na problemie bez presji widowni. Krytyka przy innych może wywołać obronę i utrudnić spokojne przyjęcie uwagi.",
  },
  {
    id: 'b2-zasada-l',
    zadanieId: B2,
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: "Jakie ryzyko dla przyjęcia uwagi niesie publiczne krytykowanie pracownika?",
    opcje: ["Uwagę zajmuje obrona wizerunku zamiast analiza błędu", "Uwagę zajmuje analiza błędu zamiast obecność widowni", "Uwagę zajmuje plan poprawy zamiast obrona wizerunku"],
    poprawna: 0,
    wyjasnienie: 'Reakcja obronna utrudnia wyciągnięcie wniosków — dlatego krytyka w cztery oczy.',
  },
  {
    id: 'b2-zasada2',
    zadanieId: B2,
    etap: 'zasada',
    rodzaj: 'wybor',
    zPamieci: true,
    pytanie: "Jaką korzyść może przynieść publiczna pochwała pracownika, który dobrze ją przyjmuje?",
    opcje: [
      'Docenienie wysiłku i dobry przykład dla zespołu',
      'Zastąpienie oceny efektów porównaniem osób',
      'Usunięcie potrzeby dalszej informacji zwrotnej',
    ],
    poprawna: 0,
    wyjasnienie: 'Klucz CKE: pochwały publiczne są doceniane i zachęcają innych do naśladowania.',
  },
  {
    id: 'b2-decyzja',
    zadanieId: B2,
    etap: 'fragment',
    rodzaj: 'wybor',
    wariant: 'decyzja',
    pytanie: 'Pracownik wykonał świetny projekt, ale źle znosi publiczne wzmianki o sobie. Co robi szef według tekstu?',
    opcje: [
      'Przekazuje pochwałę w rozmowie indywidualnej',
      'Przekazuje pochwałę podczas zebrania zespołu',
      'Rezygnuje z przekazania pozytywnej informacji',
    ],
    poprawna: 0,
    wyjasnienie: 'To drugi wyjątek z tekstu: nie wszyscy lubią publiczne pochwały.',
  },
  {
    kontekst: "Trzeba wyjaśnić skutek obu sposobów komunikacji: publicznego chwalenia i prywatnego przekazywania krytyki.",
    id: 'b2-blad',
    zadanieId: B2,
    etap: 'sprawdzenie',
    rodzaj: 'blad',
    pytanie: "Który wiersz nie wyjaśnia, jak ten sposób komunikacji wpływa na pracownika?",
    linie: [
      'Chwalenie publiczne: pracownik czuje się doceniony, a inni widzą dobry przykład.',
      'Krytykowanie w cztery oczy: bo tak jest grzeczniej.',
    ],
    bledna: 1,
    wyjasnienie: '„Grzeczniej” nie wyjaśnia skutku. Klucz: publiczna krytyka wywołuje reakcję obronną i obniża motywację.',
  },
  {
    id: 'b2-zadanie',
    zadanieId: B2,
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Zadanie 5.2: podaj dwa opisane w tekście wyjątki od zasady.',
    koniec: {
      typ: 'otwarta',
      kryteria: ['Wyjątek 1: drobne poprawki można przekazać publicznie', 'Wyjątek 2: nie wszyscy lubią publiczne pochwały'],
      slowa: [
        ['poprawk', 'drobn', 'literówk', 'dyskusj', 'nie zgadzam'],
        ['nie wszyscy', 'niektórzy', 'kar', 'preferencj', 'nie lubi', 'nie lubią'],
      ],
      wzorcowe: [
        '1. Drobne poprawki, niebędące osobistą krytyką, mogą być przekazywane publicznie.',
        '2. Nie wszyscy lubią być chwaleni publicznie — powinno się to uszanować.',
      ],
    },
    wyjasnienie: 'Klucz CKE: 1 pkt za poprawne wskazanie dwóch wyjątków.',
  },
  {
    podpowiedz: "W macierzy Eisenhowera ważność oznacza wpływ na cele, a pilność — presję czasu. Ważne, niepilne działania wymagają zaplanowania czasu.",
    id: 'b2-pomoc',
    zadanieId: null,
    etap: 'pomocnicze',
    rodzaj: 'wybor',
    pytanie: 'Macierz Eisenhowera: zadanie ważne, ale niepilne (np. regularna nauka do matury). Co z nim zrobić?',
    opcje: ['Wyznaczyć termin wykonania', 'Wykonać przed innymi zadaniami', 'Skreślić z planowanych działań'],
    poprawna: 0,
    wyjasnienie: 'Ważne i niepilne — planujemy. Natychmiast robimy to, co ważne i pilne.',
  },
  // --- powtórka: inne zadanie CKE (zad. 2 z tego arkusza — praca zespołowa)
  {
    podpowiedz: "Porównaj wynik współpracy z sumą wyników, jakie te same osoby uzyskałyby pracując osobno.",
    id: 'b2-p1',
    zadanieId: 'biz-2604-2',
    etap: 'zasada',
    rodzaj: 'wybor',
    pytanie: 'Na czym polega „efekt synergii” w pracy zespołowej?',
    opcje: [
      'Wynik zespołu przewyższa sumę wyników indywidualnych',
      'Wynik zespołu odpowiada sumie wyników indywidualnych',
      'Wynik zespołu odpowiada wynikowi najlepszego członka',
    ],
    poprawna: 0,
    wyjasnienie: 'Klucz CKE: współpraca może dać większe efekty niż suma efektów indywidualnych działań.',
  },
  {
    id: 'b2-p2',
    zadanieId: 'biz-2604-2',
    etap: 'zadanie',
    rodzaj: 'zadanie',
    zPamieci: true,
    pytanie: 'Zadanie 2: wyjaśnij własnymi słowami, na czym polega efekt synergii.',
    koniec: {
      typ: 'otwarta',
      kryteria: ['Mowa o współpracy / zespole', 'Efekt większy niż suma działań osobno'],
      slowa: [
        ['współprac', 'zespó', 'zespol', 'razem', 'wspóln'],
        ['więcej niż', 'większ', 'suma', 'sumy'],
      ],
      wzorcowe: [
        'Efekt synergii polega na tym, że współpraca w zespole może dać większe efekty niż suma efektów indywidualnych działań poszczególnych członków zespołu.',
      ],
    },
    wyjasnienie: 'Porównaj z odpowiedzią z klucza CKE.',
  },
];

// ============================================================ Lekcje

export const LEKCJE: Lekcja[] = [
  {
    skillId: 'num-order',
    przedmiot: 'math',
    tytul: 'Ułamki i kolejność działań',
    zadanieId: M1,
    seria: ['m1-polecenie', 'm1-kolejnosc', 'm1-potega', 'm1-blad', 'm1-nawias', 'm1-odwrotnosc', 'm1-kwadrat', 'm1-sprawdz', 'm1-zadanie'],
    powtorka: ['m1-p1', 'm1-p2', 'm1-p3'],
    karty: ULAMKI,
  },
  {
    skillId: 'num-powers',
    przedmiot: 'math',
    tytul: 'Potęgi o wykładniku całkowitym',
    zadanieId: M2,
    seria: ['m2-polecenie', 'm2-dane', 'm2-dane2', 'm2-ujemna', 'm2-blad', 'm2-f1', 'm2-f2', 'm2-f3', 'm2-sprawdz', 'm2-zadanie'],
    powtorka: ['m2-p1', 'm2-p2', 'm2-p3'],
    karty: POTEGI,
  },
  {
    skillId: 'cs-py-basics',
    przedmiot: 'cs',
    tytul: 'Zmienne, typy i działania',
    zadanieId: C1,
    seria: ['c1-polecenie', 'c1-zasada', 'c1-kod1', 'c1-blad', 'c1-kolejnosc', 'c1-kod2', 'c1-slad', 'c1-sprawdz', 'c1-zadanie'],
    powtorka: ['c1-p1', 'c1-p2'],
    karty: ZMIENNE,
    luki: ['Pełne zadanie 2.1 wymaga też pętli (następna lekcja kursu) — tu śledzimy ją krok po kroku.'],
  },
  {
    skillId: 'cs-py-conditions',
    przedmiot: 'cs',
    tytul: 'Warunki i wyrażenia logiczne',
    zadanieId: C2,
    seria: ['c2-polecenie', 'c2-dane', 'c2-kod1', 'c2-warunek', 'c2-kod2', 'c2-blad', 'c2-kod3', 'c2-sprawdz', 'c2-zadanie'],
    powtorka: ['c2-p1', 'c2-p2'],
    karty: WARUNKI,
  },
  {
    skillId: 'biz-entrepreneurship',
    przedmiot: 'biz',
    tytul: 'Przedsiębiorczość i innowacje',
    zadanieId: B1,
    seria: ['b1-polecenie', 'b1-dane', 'b1-zasada', 'b1-decyzja', 'b1-blad', 'b1-zadanie', 'b1-pomoc'],
    powtorka: ['b1-p1', 'b1-p2'],
    karty: PRZEDSIEBIORCZOSC,
    luki: [
      'Rodzaje i źródła innowacji — ani arkusz pokazowy 2026, ani informator CKE nie mają o tym zadania. Karta o innowacjach jest oznaczona jako ćwiczenie pomocnicze.',
      'Patriotyzm gospodarczy — brak autentycznego zadania CKE.',
    ],
  },
  {
    skillId: 'biz-soft-skills',
    przedmiot: 'biz',
    tytul: 'Komunikacja, wpływ, czas i decyzje',
    zadanieId: B2,
    seria: ['b2-polecenie', 'b2-dane', 'b2-zasada', 'b2-zasada2', 'b2-decyzja', 'b2-blad', 'b2-zadanie', 'b2-pomoc'],
    powtorka: ['b2-p1', 'b2-p2'],
    karty: KOMUNIKACJA,
    luki: [
      'Techniki wywierania wpływu i obrona przed manipulacją — brak autentycznego zadania CKE.',
      'Zarządzanie czasem (macierz Eisenhowera) — brak autentycznego zadania CKE; karta pomocnicza wyraźnie oznaczona.',
      'Etapy podejmowania decyzji — pośrednio w zadaniu 1 (decyzja Agnieszki), bez osobnego zadania CKE.',
    ],
  },
];

export const lekcja = (skillId: string): Lekcja | undefined => LEKCJE.find((l) => l.skillId === skillId);

export function karta(l: Lekcja, id: string): Karta {
  const k = l.karty.find((x) => x.id === id);
  if (!k) throw new Error(`Brak karty ${id} w lekcji ${l.skillId}`);
  return k;
}
